import { Box, Card, Divider, useTheme } from '@mui/material'
import Typography from '@mui/material/Typography'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dashboardApi } from '../../api/swagger'
import useEndpoint from '../../hooks/useEndpoint'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import {
  FetchMetricMetricIdEnum,
  FetchMetricsResponseBodyMetrics,
} from '../../swagger'
import { useUser } from '../../UserContext'
import { normalizeInput } from '../../utils/bigJSHelper'
import ContainedButton, {
  ContainedButtonVariant,
} from '../Buttons/ContainedButton'
import CardFormHeader from '../Card/CardFormHeader'
import { LoadingContext } from '../Context/LoadingContext'
import DynamicBreadcrumbs from '../Elements/DynamicBreadcrumbs'
import Header, { HeaderVariant } from '../Elements/Header'
import { InfoTooltip } from '../Elements/InfoTooltip'
import LoadingProgress from '../Elements/LoadingProgress'
import SpaceBetweenSection from '../Elements/SpaceBetweenSection'
import { getDashboardColor } from './utils/getDashboardColor'
import { getMetricIdForRoute } from './utils/getMetricIdForRoute'
import { useAcademicYears } from '../../hooks/useAcademicYears'
import DropDown, { DropDownVariant } from '../Menus/DropDown'

const DashboardSummary: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const abortController = new AbortController()

  const showOnDesktop = useShowOnDesktop()
  const {
    DashboardTab: { fetchMetrics },
  } = useLoadingIds()

  const { loadingIds } = React.useContext(LoadingContext)
  const { selectedActorRole } = useUser()

  const actorRoleValid = useMemo(
    () =>
      `${selectedActorRole?.subtitle && '/'} ${
        selectedActorRole?.subtitle ?? ''
      }`,
    [selectedActorRole?.subtitle]
  )

  const actorRoleName = useMemo(
    () => `${selectedActorRole?.name} ${actorRoleValid}`,
    [actorRoleValid, selectedActorRole?.name]
  )

  const [metrics, setMetrics] = useState<FetchMetricsResponseBodyMetrics[]>()
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<number>()

  const { academicYears, loadingId: academicYearsLoadingId } = useAcademicYears(
    {
      errorMessage: t(
        'DashboardDetails.Error.FetchMetrics',
        'An error occurred while retrieving academic years.'
      ),
    }
  )

  // Reset selected academic year when actor changes
  useEffect(() => {
    setSelectedAcademicYear(undefined)
    setMetrics(undefined)
  }, [selectedActorRole?.id])

  useEffect(() => {
    // Wait for academic years API to complete before initializing
    if (loadingIds.has(academicYearsLoadingId)) return

    const storedYearString = localStorage.getItem('dashboardAcademicYear')
    const storedYear = storedYearString ? Number(storedYearString) : undefined

    if (storedYear && academicYears.length > 0) {
      const isValidStoredYear = academicYears.some(
        (year) => year.year === storedYear
      )

      if (isValidStoredYear) {
        setSelectedAcademicYear(storedYear)
        return
      }
    }

    // Only set a year if academicYears has data
    if (academicYears.length > 0) {
      const current = academicYears.find(
        (academicYear) =>
          academicYear.name.includes('CURRENT') || academicYear.isDefault
      )

      if (current?.year != null) {
        setSelectedAcademicYear(current.year)
      } else {
        const fallbackYear = academicYears[0]?.year ?? new Date().getFullYear()
        setSelectedAcademicYear(fallbackYear)
      }
    }
    // If academicYears is empty, selectedAcademicYear stays undefined
  }, [academicYears, loadingIds, academicYearsLoadingId])

  const { triggerFetch } = useEndpoint({
    loadingId: fetchMetrics,
    abortController,
    swaggerCall: async (abortController?: AbortController) => {
      const response = await dashboardApi.fetchMetrics({
        $signal: abortController?.signal,
        academicYear: selectedAcademicYear,
      })
      setMetrics(response.metrics)
    },
  })

  useEffect(() => {
    if (selectedAcademicYear === undefined) return
    triggerFetch()
  }, [triggerFetch, selectedAcademicYear])

  const handleAcademicYearSelection = (selection: string) => {
    const selectedYear = academicYears.find(
      (year) => year.name === selection
    )?.year

    const yearToSet = selectedYear ?? academicYears[0]?.year

    localStorage.setItem('dashboardAcademicYear', yearToSet?.toString() ?? '')

    if (yearToSet != null) {
      setSelectedAcademicYear(yearToSet)
    }
  }

  const dropdownValue = useMemo(() => {
    return (
      academicYears.find((year) => year.year === selectedAcademicYear)?.name ??
      ''
    )
  }, [academicYears, selectedAcademicYear])

  const Metrics = useMemo(() => {
    return metrics?.map(({ metricName, values, style, actionLink }) => {
      return (
        <Box key={metricName}>
          <Divider />
          <Box
            sx={{
              marginTop: theme.spacing(2),
              marginLeft: theme.spacing(2),
              marginBottom: theme.spacing(2),
            }}
          >
            <SpaceBetweenSection
              containerStyles={{
                [theme.breakpoints.down('md')]: {
                  flexDirection: 'column',
                },
              }}
              leftStyle={{
                width: '60%',
              }}
              left={
                <Typography
                  variant="subtitle2"
                  color={theme.palette.primary.dark}
                >
                  {metricName === 'Licensing Balance'
                    ? t(
                        'DashboardSummary.MetricName.LicenseBalance',
                        'License Balance'
                      )
                    : metricName}
                  :
                </Typography>
              }
              center={
                <Typography
                  variant="body2"
                  component="span"
                  fontWeight={700}
                  sx={{
                    color: getDashboardColor(style, theme),
                    typography: 'h5',
                  }}
                >
                  {FetchMetricMetricIdEnum.LicensingDue ===
                  getMetricIdForRoute(actionLink)
                    ? normalizeInput(values[0])
                    : values[0]?.toString() || ''}
                </Typography>
              }
              right={
                <ContainedButton
                  id={metricName}
                  css={{
                    [theme.breakpoints.down('md')]: {
                      marginLeft: 0,
                    },
                    whiteSpace: 'nowrap',
                  }}
                  variant={ContainedButtonVariant.ClickForDetails}
                  to={`${actionLink}?academicYear=${selectedAcademicYear}`}
                  disabled={values.every((value) => value === 0)}
                />
              }
            />
          </Box>
        </Box>
      )
    })
  }, [metrics, theme, t, selectedAcademicYear])

  // Show loading spinner if:
  // 1. Academic years are still loading, OR
  // 2. Metrics are actively loading, OR
  // 3. We have a selected year but metrics haven't loaded yet
  const isAcademicYearsLoading = loadingIds.has(academicYearsLoadingId)
  const isMetricsLoading = loadingIds.has(fetchMetrics)
  const hasSelectedYearButNoMetrics =
    selectedAcademicYear !== undefined && metrics === undefined

  if (
    isAcademicYearsLoading ||
    isMetricsLoading ||
    hasSelectedYearButNoMetrics
  ) {
    return <LoadingProgress />
  }

  return (
    <>
      <DynamicBreadcrumbs
        breadcrumbs={[
          {
            onClick: undefined,
            label: t(
              'Account.Dashboard.Breadcrumb',
              '{{actorRoleName}} - Dashboard',
              { actorRoleName }
            ),
          },
        ]}
      />
      {selectedAcademicYear === undefined ? (
        <Card
          sx={{
            marginTop: theme.spacing(4),
            marginBottom: theme.spacing(4),
            padding: theme.spacing(6),
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" component="h3">
            {t('DashboardSummary.NoMetrics.Title', 'No Metrics Available')}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.palette.primary.dark,
            }}
          >
            {t(
              'DashboardSummary.NoMetrics.Message',
              'There are currently no metrics to display.'
            )}
          </Typography>
        </Card>
      ) : (
        <>
          <Box marginBottom="5px">
            <DropDown
              id="academicYearFilter"
              menuOptions={academicYears.map(({ year, name }) => {
                return { name: name, id: year }
              })}
              handleSelection={handleAcademicYearSelection}
              value={dropdownValue}
              variant={DropDownVariant.SortAndFilter}
            />
          </Box>
          <Card
            sx={{
              color: theme.palette.primary.main,
              marginBottom: theme.spacing(4),
              mt: theme.spacing(4),
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              minWidth="25%"
              alignContent={'center'}
              px={showOnDesktop ? 0 : 4}
            >
              <CardFormHeader
                header={
                  <Box>
                    <Header
                      id="dashboardHeader"
                      headerName={t(
                        'DashboardCard.Header.CurrentStatistics',
                        'Current Statistics'
                      )}
                      component="h3"
                      variant={HeaderVariant.Card}
                      endAdornment={
                        <InfoTooltip
                          message={t(
                            'DashboardCard.Header.HyperLinkInstruction',
                            'Select CLICK FOR DETAILS button for any of the links below'
                          )}
                        />
                      }
                    />
                  </Box>
                }
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mx: theme.spacing(4),
                width: '50%',
                [theme.breakpoints.down('md')]: {
                  width: '80%',
                  margin: '0 auto',
                },
              }}
            >
              {Metrics}
            </Box>
          </Card>
        </>
      )}
    </>
  )
}

export default DashboardSummary
